/*
 * INTELLIGENCE (clone) — four-layer status, Collective Improvement governance, Model Upgrade governance.
 * Reads/writes /api/intelligence/*. Every control is enforced server-side (approver identity, Founder-only actions,
 * two distinct people per approval, Preview-only). Nothing here can reach real members or production.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Brain, GitBranch, ShieldCheck, Cpu } from "lucide-react";
import { Panel, Note, Chip } from "./ConsolePrimitives";
import "../../styles/founder-console.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/intelligence`;
const TONE = { operational: "ok", active: "ok", ready: "warn", not_started: "quiet", unavailable: "bad" };
const LAYER_NAMES = {
  personal_memory: "Personal Memory", knowledge_research: "Knowledge & Research",
  collective_improvement: "Collective Improvement", model_upgrade: "Model Upgrade",
};

const Syn = ({ testId }) => <Chip tone="warn" testId={testId}>synthetic</Chip>;

const Btn = ({ onClick, children, testId, busy, ghost }) => (
  <button className={`fc-btn ${ghost ? "fc-btn--ghost" : ""}`} onClick={onClick} disabled={busy} data-testid={testId}
          style={{ marginRight: 8, marginTop: 6 }}>{children}</button>
);

const Layers = ({ layers }) => (
  <Panel title="Intelligence layers" icon={Brain} testId="intel-layers">
    {Object.entries(layers).map(([k, v]) => (
      <div key={k} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "baseline", margin: "6px 0" }} data-testid={`intel-layer-${k}`}>
        <strong style={{ minWidth: 180 }}>{LAYER_NAMES[k]}</strong>
        <Chip tone={TONE[v.state]} testId={`intel-layer-${k}-state`}>{v.state.replace("_", " ")}</Chip>
        <span className="fc-note" style={{ margin: 0, flexBasis: "100%", overflowWrap: "anywhere" }}>{typeof v.detail === "string" ? v.detail : JSON.stringify(v.detail)}</span>
      </div>
    ))}
  </Panel>
);

const Collective = ({ ci, role, act, busy }) => (
  <Panel title="Collective Improvement governance" icon={GitBranch} testId="intel-ci">
    <Note testId="intel-ci-counts">Contributors: <strong>{ci.contributors}</strong> <Syn testId="intel-syn-contributors" /> (synthetic accounts, not real members) ·
      enrolled members: <strong>{ci.enrolled_members}</strong> <Syn testId="intel-syn-enrolled" /> (test accounts) ·
      guidance {ci.enabled ? "enabled" : "disabled"} · live version: {ci.active_kv || "none"} <Syn testId="intel-syn-version" /></Note>
    {role === "founder" && !ci.findings.length && <Btn testId="intel-ci-cycle" busy={busy} onClick={() => act("ci/cycle")}>Run synthetic contribution cycle</Btn>}
    <div style={{ overflowX: "auto", maxWidth: "100%" }}><table className="fc-table" data-testid="intel-ci-findings"><tbody>
      {ci.findings.map((f) => {
        const req = ci.requests.find((r) => r.target === f.finding_id);
        return (
          <tr key={f.finding_id} data-testid={`intel-finding-${f.finding_id}`}>
            <td>{f.dimension} · {f.value} · {f.language} <Syn testId={`intel-syn-finding-${f.finding_id}`} /></td>
            <td><Chip tone={f.state === "approved" ? "ok" : "quiet"}>{f.state}</Chip></td>
            <td>{req ? `${req.signed_by.map((p) => `${p} (clone dev key)`).join(" + ") || "no signatures"} (${req.signed_by.length}/${req.required}) · not independent human review` : ""}</td>
            <td>
              {req && <Btn testId={`intel-sign-${f.finding_id}`} busy={busy} onClick={() => act("ci/sign", { request_id: req.request_id })}>Approve</Btn>}
              {["proposed", "approved"].includes(f.state) && <Btn ghost testId={`intel-reject-${f.finding_id}`} busy={busy} onClick={() => act("ci/reject", { finding_id: f.finding_id })}>{f.state === "proposed" ? "Reject" : "Retire"}</Btn>}
            </td>
          </tr>);
      })}
      {ci.requests.filter((r) => r.action === "activate_release").map((r) => (
        <tr key={r.request_id} data-testid={`intel-release-${r.kind}`}>
          <td>Release ({r.kind === "g3" ? "Gate 3" : "Gate 3 add-on"})</td><td><Chip tone="warn">awaiting approval</Chip></td>
          <td>{r.signed_by.map((p) => `${p} (clone dev key)`).join(" + ") || "no signatures"} ({r.signed_by.length}/{r.required}) · not independent human review</td>
          <td><Btn testId={`intel-sign-release-${r.kind}`} busy={busy} onClick={() => act("ci/sign", { request_id: r.request_id })}>Approve release</Btn></td>
        </tr>))}
    </tbody></table></div>
    <div style={{ marginTop: 10 }}>
      {role === "founder" && <Btn testId="intel-ci-publish" busy={busy} onClick={() => act("ci/publish")}>Publish guidance</Btn>}
      <Btn ghost testId="intel-ci-rollback" busy={busy} onClick={() => act("ci/rollback")}>Roll back to no guidance</Btn>
      <Btn ghost testId="intel-ci-shutdown" busy={busy} onClick={() => act("ci/shutdown", { on: ci.enabled })}>{ci.enabled ? "Emergency shutdown" : "Resume (Founder)"}</Btn>
    </div>
    <div style={{ overflowX: "auto", maxWidth: "100%" }}><table className="fc-table" data-testid="intel-ci-versions" style={{ marginTop: 10 }}><tbody>
      {ci.versions.map((v) => (
        <tr key={v.kv_id}><td>{v.kv_id} <Syn testId={`intel-syn-kv-${v.kv_id}`} /></td><td><Chip tone={v.state === "active" ? "ok" : "quiet"}>{v.state}</Chip></td><td>{v.items.join(", ")}</td>
          <td>{role === "founder" && v.state !== "active" && <Btn ghost testId={`intel-restore-${v.kv_id}`} busy={busy} onClick={() => act("ci/rollback", { kv_id: v.kv_id })}>Restore</Btn>}</td></tr>))}
    </tbody></table></div>
    <Note testId="intel-ci-events"><Syn testId="intel-syn-events" /> Recent governance (clone identities and test clients): {ci.events.slice(0, 8).map((e) => `${e.action} (${e.actor}${e.detail?.via ? `, ${e.detail.via}` : ""})`).join(" · ") || "none"}</Note>
  </Panel>
);

const Models = ({ m, role, act, busy }) => (
  <Panel title="Model Upgrade governance" icon={Cpu} testId="intel-models">
    <Note>Message model now: <strong data-testid="intel-active-model">{m.active_message_model}</strong> (documented baseline {m.baseline_message}). Switching is development-only; production keeps the baseline.</Note>
    <div style={{ overflowX: "auto", maxWidth: "100%" }}><table className="fc-table" data-testid="intel-model-inventory"><tbody>
      {m.inventory.map((r) => <tr key={r.surface}><td>{r.surface}</td><td>{r.confirmed}</td><td className="fc-note">{r.note || ""}</td></tr>)}
    </tbody></table></div>
    <div style={{ marginTop: 10 }}>
      {["gpt-5.2", "gpt-5.4-mini"].map((model) => <Btn ghost key={model} testId={`intel-register-${model}`} busy={busy} onClick={() => act("models/register", { model })}>Register {model} as candidate</Btn>)}
      <Btn ghost testId="intel-model-rollback" busy={busy} onClick={() => act("models/rollback")}>Restore previous model</Btn>
    </div>
    {m.candidates.map((c) => (
      <div key={c.candidate_id} className="fc-panel" style={{ marginTop: 10 }} data-testid={`intel-candidate-${c.candidate_id}`}>
        <strong>{c.model}</strong> vs {c.baseline} · <Chip tone="quiet">{c.state}</Chip>
        {c.evaluation && <Note><Syn testId={`intel-syn-eval-${c.candidate_id}`} /> synthetic evaluation set · {c.evaluation.recommendation} — baseline {JSON.stringify(c.evaluation.baseline)} · candidate {JSON.stringify(c.evaluation.candidate)} · judge {JSON.stringify(c.evaluation.warmth_personality_judge)}</Note>}
        {c.state === "registered" && <Btn testId={`intel-evaluate-${c.candidate_id}`} busy={busy} onClick={() => act("models/evaluate", { candidate_id: c.candidate_id })}>Run evaluation</Btn>}
        {c.state === "evaluated" && role === "founder" && <Btn testId={`intel-switch-${c.candidate_id}`} busy={busy} onClick={() => act("models/switch", { candidate_id: c.candidate_id })}>Authorize development-only switch</Btn>}
      </div>))}
  </Panel>
);

export const IntelligenceView = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/status`).then((r) => { setData(r.data); setError(null); })
    .catch((e) => setError(e.response?.data?.detail || "Unavailable")), []);
  useEffect(() => { load(); }, [load]);
  const act = async (path, body = {}) => {
    setBusy(true);
    try { await axios.post(`${API}/${path}`, body); await load(); } catch (e) { setError(e.response?.data?.detail || "Action refused"); }
    setBusy(false);
  };
  if (error && !data) return <Note testId="intel-error">{error}</Note>;
  if (!data) return <Note testId="intel-loading">Loading…</Note>;
  return (
    <div data-testid="intel-view" style={{ maxWidth: "100%" }}>
      <Chip tone="warn" testId="intel-clone-banner">Isolated clone · synthetic members only · signed in as {data.role}</Chip>
      <Note testId="intel-approval-evidence">{data.approval_evidence}</Note>
      {error && <Note testId="intel-action-error">{error}</Note>}
      <Layers layers={data.layers} />
      <Collective ci={data.collective} role={data.role} act={act} busy={busy} />
      <Models m={data.models} role={data.role} act={act} busy={busy} />
      <Panel title="Research privacy (last outbound lookups)" icon={ShieldCheck} testId="intel-research">
        <Note testId="intel-research-scope"><Syn testId="intel-syn-research" /> Lookups made in this clone by synthetic and test accounts.</Note>
        {data.research_outbound.length ? data.research_outbound.map((r, i) => (
          <Note key={i}>{r.withheld ? "Withheld — nothing sent" : `Sent: “${r.sent}”`}{r.removed.length ? ` · removed: ${r.removed.join(", ")}` : ""}</Note>))
          : <Note>No lookups since the server started.</Note>}
      </Panel>
    </div>
  );
};

export default function IntelligenceReviewPage() {
  return <div className="fc" style={{ padding: 24, display: "block" }}><IntelligenceView /></div>;
}
